import {
  KeplerTableField,
  LayerPopupSettings,
  PopupEvent,
  PopupField,
  PopupRow,
  PopupStyle
} from 'features/builder/types'
import { formatAggregatedField, getColumnAliasForAggregationExp } from 'features/builder/state/spatialIndexUtils'
import { ProviderType } from 'features/connections'
import { AGGREGATION_TYPES } from '@carto/kepler.gl/constants'
import DOMPurify from 'dompurify'
import { SchemaField } from '@carto/api-client'

/**
 * Popups for click or hover events are enabled only if preconditions are met:
 * interactions are enabled, style is not 'None', and at least one field
 * is populated in the field list or template.
 */
export function isLayerPopupEnabled(
  event: PopupEvent,
  settings?: LayerPopupSettings
): settings is LayerPopupSettings & { enabled: true } {
  if (!settings || !settings.enabled || !settings[event]) {
    return false
  }

  if (settings[event].templateMode) {
    return hasTemplateFields(settings[event].template)
  }

  if (settings[event].style === PopupStyle.None) {
    return false
  }

  const fields = settings[event].fields
  return fields ? fields.length > 0 : false
}

const expressionRegexp = /[()+*/%&|?:.-]/

export function isTemplateExpression(value: string): boolean {
  return value.match(expressionRegexp) !== null
}

/**
 * Encodes a {@link PopupField} to a row/data key, e.g. "SUM(my_field)".
 */
export function encodePopupFieldRowKey(field: PopupField): string {
  if (field.spatialIndexAggregation) {
    return formatAggregatedField(field.name, field.spatialIndexAggregation)
  }
  return field.name
}

/** Decodes a row/data key (e.g. "SUM(my_field)") to a {@link PopupField}. */
export function decodePopupFieldRowKey(): PopupField {
  throw new Error('Not implemented')
}

/**
 * Encodes a {@link PopupField} to a string key representing it in an HTML template,
 * e.g. "my_field_sum".
 */
export function encodePopupFieldTemplateKey(field: PopupField, provider: ProviderType): string {
  if (field.spatialIndexAggregation) {
    return getColumnAliasForAggregationExp(field.name, field.spatialIndexAggregation, provider)
  }
  return field.name
}

/**
 * Decodes a template key (e.g. "my_field_sum") to a {@link PopupField}. Schema is
 * required to distinguish whether "my_field_sum" is a real column or an aggregation.
 */
export function decodePopupFieldTemplateKey(key: string, fields: KeplerTableField[] | SchemaField[]): PopupField {
  return _decodePopupFieldTemplateKey(key, fields)
}

/**
 * Internal helper allowing schema to be omitted. Only intended for direct use
 * in {@link renderTemplate}, which checks first for an exact column match and
 * then decodes — without schema — to look for aggregated columns. Omitting
 * schema in other cases, without that fallback, would not be safe.
 *
 * @deprecated TODO(sc-496470): Prefer {@link decodePopupFieldTemplateKey} using schema.
 */
function _decodePopupFieldTemplateKey(key: string, fields?: KeplerTableField[] | SchemaField[]): PopupField {
  const field: PopupField = { name: key }

  // Check whether 'my_field_count' is a real column or an aggregation of 'my_field'. See sc-496470.
  const availableFieldNames = fields && new Set(getFieldNames(fields))
  if (!availableFieldNames || !availableFieldNames.has(key)) {
    for (const aggregation of Object.values(AGGREGATION_TYPES)) {
      if (key.endsWith(`_${aggregation}`)) {
        field.name = key.replace(new RegExp(`_${aggregation}$`), '')
        field.spatialIndexAggregation = aggregation
        break
      }
    }
  }

  if (isTemplateExpression(key)) {
    field.isExpression = true
  }

  return field
}

const getFieldNames = (fields: KeplerTableField[] | SchemaField[]): string[] => {
  return fields.map((field: KeplerTableField | SchemaField) => {
    return (field as KeplerTableField).id || (field as SchemaField).name
  })
}

const placeholderRegexp = /{{\s*[\w\s()+*/%&|?:.-]+\s*}}/g
const placeholderToFieldTemplateKey = (placeHolder: string) => placeHolder.replace(/{|}|\s/g, '')

export const getFieldsFromTemplate = (template = '', fields: KeplerTableField[] | SchemaField[]): PopupField[] => {
  const fieldTokens = (template.match(placeholderRegexp) || []).map(placeholderToFieldTemplateKey)
  return Array.from(new Set(fieldTokens)).map((key) => decodePopupFieldTemplateKey(key, fields))
}

/**
 * Returns true if template contains at least one {{...}} token. Tokens are NOT
 * yet checked for errors in column names or expressions, as that requires schema.
 */
export const hasTemplateFields = (template = ''): boolean => {
  const fieldTokens = (template.match(placeholderRegexp) || []).map(placeholderToFieldTemplateKey)
  return fieldTokens.length > 0
}

export const sanitizeTemplate = (template = ''): string => {
  return DOMPurify.sanitize(template, { ADD_ATTR: ['target'] })
}

const EMPTY_DISPLAY_VALUE = '-'

export const renderTemplate = (template = '', rows: PopupRow[]): string => {
  // HTML templates are sanitized on save for user visibility, and sanitized
  // again before rendering (avoid trust that stored HTML is safe from XSS).
  return sanitizeTemplate(template).replaceAll(placeholderRegexp, (placeholder) => {
    // TODO(sc-496470): Remove the trial-and-error below. Requires schema and
    // provider type, not yet available when rendering templates

    // HTML templates support aggregated fields as 'my_field_sum', but real
    // fields may also be named with suffixes like '_sum' or '_count'. We
    // don't have access to the schema while rendering a template, so try
    // looking for the exact token in the row first.
    const templateKey = placeholderToFieldTemplateKey(placeholder)
    let row = rows.find((row) => row.field.name === templateKey)
    if (row) {
      return row.value.displayValue || EMPTY_DISPLAY_VALUE
    }

    // If an exact match wasn't found above, assume this is an aggregation.
    // Call decodePopupFieldTemplateKey without schema (assumes 'my_field_sum'
    // is an aggregation) then creates a row key, like 'SUM(my_field)'.
    const field = _decodePopupFieldTemplateKey(templateKey)
    const rowKey = encodePopupFieldRowKey(field)
    row = rows.find((row) => row.field.name === rowKey)
    return row?.value.displayValue || EMPTY_DISPLAY_VALUE
  })
}
